'use client';

import * as React from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { Box, Button, Card, CardContent, Chip, Typography, Link as MuiLink } from '@mui/material';
import { ProCheckoutButton } from '@/components/billing/ProCheckoutButton';
import { SUPPORT_EMAIL } from '@/lib/site-contact';

export function DashboardBillingCard() {
  const { data: session, update } = useSession();
  const plan = session?.user?.plan ?? 'FREE';
  const isPaidSubscription =
    plan === 'CREATOR' || plan === 'PRO' || plan === 'ENTERPRISE' || plan === 'WHITE_LABEL';

  const planLabel =
    plan === 'ENTERPRISE'
      ? 'Enterprise'
      : plan === 'PRO'
        ? 'Pro'
        : plan === 'CREATOR'
          ? 'Creator'
          : plan === 'WHITE_LABEL'
            ? 'Standard White Label'
            : plan;
  const periodEnd = session?.user?.stripeCurrentPeriodEnd;
  const customerId = session?.user?.stripeCustomerId;
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [portalError, setPortalError] = React.useState<string | null>(null);

  const nextBillingLabel = periodEnd
    ? new Date(periodEnd).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  const openPortal = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const res = await fetch('/api/stripe/manage-subscription', { method: 'POST' });
      const data = (await res.json().catch(() => ({}))) as { url?: string; message?: string };
      if (!res.ok) {
        setPortalError(data.message || 'Could not open billing portal.');
        return;
      }
      if (data.url) window.location.href = data.url;
      else setPortalError('No portal URL returned.');
    } catch {
      setPortalError('Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Current plan: {planLabel}
        </Typography>
        {isPaidSubscription && nextBillingLabel ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Next billing date: {nextBillingLabel}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isPaidSubscription
              ? 'Billing details will appear here after checkout completes.'
              : 'Start a free trial of Pro — card required; you will not be charged until the trial ends.'}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {!isPaidSubscription ? (
            <Box sx={{ minWidth: 200, flex: '1 1 auto' }}>
              <ProCheckoutButton billing="monthly" variant="contained" color="primary" size="medium">
                Start Pro free trial
              </ProCheckoutButton>
            </Box>
          ) : null}
          {customerId ? (
            <Button
              type="button"
              variant="outlined"
              disabled={portalLoading}
              onClick={() => void openPortal()}
            >
              {portalLoading ? 'Opening…' : 'Manage billing'}
            </Button>
          ) : null}
          {isPaidSubscription ? (
            <Button type="button" variant="text" size="small" onClick={() => void update({})}>
              Refresh plan
            </Button>
          ) : null}
        </Box>
        {portalError ? (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }} role="alert">
            {portalError}
          </Typography>
        ) : null}
        {!isPaidSubscription ? (
          <Chip
            label={`${process.env.NEXT_PUBLIC_STRIPE_PRO_TRIAL_DAYS ?? '14'}-day trial · then paid`}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        ) : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Refunds aren&apos;t issued in this screen. Email{' '}
          <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
            {SUPPORT_EMAIL}
          </MuiLink>{' '}
          or use{' '}
          <MuiLink component={Link} href="/contact" color="primary">
            Contact
          </MuiLink>{' '}
          (subject: Billing or refund). Eligibility: first paid charge only (14 days), not each renewal—see{' '}
          <MuiLink component={Link} href="/faq" color="primary">
            FAQ
          </MuiLink>
          .
        </Typography>
      </CardContent>
    </Card>
  );
}
